import { useEffect } from "react";
import { Users, Clock, Award, UserCheck, Smile } from 'lucide-react';

function HospitalityRetailPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const benefits = [
    {
      icon: <Users className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Skilled Professionals",
      description: "Access to trained hospitality and retail experts with excellent customer service skills"
    },
    {
      icon: <Clock className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Flexible Staffing",
      description: "Scalable workforce solutions for peak seasons, events, and daily operations"
    },
    {
      icon: <Award className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Quality Assurance",
      description: "Thoroughly vetted staff ensuring exceptional service standards"
    },
    {
      icon: <Smile className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Customer Excellence",
      description: "Dedicated professionals focused on creating memorable customer experiences"
    }
  ];

  const roles = [
    {
      title: "Front Desk Associates",
      responsibilities: ["Guest reception & check-in", "Customer inquiries", "Reservation management", "Service coordination"]
    },
    {
      title: "Retail Sales Staff",
      responsibilities: ["Customer assistance", "Product knowledge", "Sales conversion", "Inventory support"]
    },
    {
      title: "Restaurant Service Staff",
      responsibilities: ["Table service", "Order management", "Guest satisfaction", "Food & beverage knowledge"]
    },
    {
      title: "Operations Supervisors",
      responsibilities: ["Team management", "Quality control", "Staff training", "Performance optimization"]
    },
    {
      title: "Housekeeping Staff",
      responsibilities: ["Room maintenance", "Cleanliness standards", "Guest services", "Inventory management"]
    },
    {
      title: "Store Managers",
      responsibilities: ["Operations management", "Sales targets", "Staff leadership", "Customer experience"]
    }
  ];

  return (
    <div className="w-full">
      {/* Hero Banner */}
      <section className="relative h-[400px] sm:h-[500px] md:h-[600px] w-full">
        <img
          src="/assest/inside-content/group-muslim-friends-restaurant.jpg"
          alt="Hospitality & Retail Services"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
        
        <div className="container relative z-10 h-full px-5 sm:px-6 flex items-center">
          <div className="max-w-3xl">
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-4 sm:mb-6">
              Hospitality & <br />Retail Services
            </h1>
            <p className="font-body text-lg sm:text-xl md:text-2xl text-white/95 leading-relaxed max-w-2xl">
              Elevating customer experiences with exceptional hospitality and retail professionals
            </p>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="bg-white py-12 sm:py-16 md:py-20 lg:py-16">
        <div className="container mx-auto px-5 sm:px-6 lg:px-4">
          <div className="max-w-4xl mx-auto text-center">
            <span className="section-label mb-3 sm:mb-4 inline-block text-xs sm:text-sm">
              Our Expertise
            </span>
            <h2 className="font-display text-3xl sm:text-[36px] md:text-[42px] lg:text-[52px] font-bold text-brand-navy leading-tight mb-6">
              Premier Staffing Solutions for Hospitality & Retail
            </h2>
            <p className="text-text-dark text-base sm:text-lg leading-relaxed mb-8">
              With extensive experience in the hospitality and retail sectors, IMMACULATE has established 
              itself as one of the leading staffing firms in the UAE. We have a deep understanding of the 
              needs of these sectors and provide businesses with the right staff to meet their specific 
              requirements. Our professionals are trained to deliver exceptional customer service, ensuring 
              memorable experiences for your guests and customers.
            </p>
            <blockquote className="border-l-4 border-gold pl-6 my-8">
              <p className="font-display italic text-lg sm:text-xl text-text-medium leading-relaxed">
                "Excellence is not a skill, it's an attitude - and our team brings that attitude to every interaction."
              </p>
            </blockquote>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="bg-gray-50 py-12 sm:py-16 md:py-20">
        <div className="container mx-auto px-5 sm:px-6 lg:px-4">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="font-display text-3xl sm:text-[36px] md:text-[42px] font-bold text-brand-navy leading-tight">
              Why Partner With Us
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-white p-6 sm:p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300"
              >
                <div className="text-gold mb-4">
                  {benefit.icon}
                </div>
                <h3 className="font-display text-xl sm:text-2xl font-semibold text-brand-navy mb-3">
                  {benefit.title}
                </h3>
                <p className="text-text-dark text-sm sm:text-base leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roles Section */}
      <section className="bg-white py-12 sm:py-16 md:py-20 lg:py-16">
        <div className="container mx-auto px-5 sm:px-6 lg:px-4">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <span className="section-label mb-3 sm:mb-4 inline-block text-xs sm:text-sm">
              Career Opportunities
            </span>
            <h2 className="font-display text-3xl sm:text-[36px] md:text-[42px] lg:text-[52px] font-bold text-brand-navy leading-tight mb-4">
              Available Positions
            </h2>
            <p className="text-text-dark text-base sm:text-lg max-w-3xl mx-auto">
              Discover diverse career opportunities in hospitality and retail with competitive compensation 
              packages tailored to your experience and expertise.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-7xl mx-auto">
            {roles.map((role, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-white to-gray-50 p-6 sm:p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                <div className="flex items-start justify-between mb-6">
                  <h3 className="font-display text-xl sm:text-2xl font-bold text-brand-navy">
                    {role.title}
                  </h3>
                  <UserCheck className="w-6 h-6 text-gold flex-shrink-0 ml-2" />
                </div>
                
                <div className="space-y-2">
                  <h4 className="font-semibold text-brand-navy text-sm uppercase tracking-wide mb-3">
                    Key Responsibilities:
                  </h4>
                  <ul className="space-y-2">
                    {role.responsibilities.map((resp, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-gold mr-2 mt-1">•</span>
                        <span className="text-text-dark text-sm sm:text-base">{resp}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="bg-gray-50 py-12 sm:py-16 md:py-20">
        <div className="container mx-auto px-5 sm:px-6 lg:px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-display text-3xl sm:text-[36px] md:text-[42px] font-bold text-brand-navy leading-tight text-center mb-10 sm:mb-12">
              Our Service Areas
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center mb-4">
                  <Smile className="w-8 h-8 text-gold mr-3" />
                  <h3 className="font-display text-xl font-bold text-brand-navy">Hospitality</h3>
                </div>
                <ul className="space-y-2 text-text-dark">
                  <li className="flex items-start">
                    <span className="text-gold mr-2">•</span>
                    <span>Hotels & Resorts</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-2">•</span>
                    <span>Restaurants & Cafes</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-2">•</span>
                    <span>Event Venues</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-2">•</span>
                    <span>Guest Services</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center mb-4">
                  <UserCheck className="w-8 h-8 text-gold mr-3" />
                  <h3 className="font-display text-xl font-bold text-brand-navy">Retail</h3>
                </div>
                <ul className="space-y-2 text-text-dark">
                  <li className="flex items-start">
                    <span className="text-gold mr-2">•</span>
                    <span>Shopping Malls</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-2">•</span>
                    <span>Luxury Boutiques</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-2">•</span>
                    <span>Supermarkets</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-2">•</span>
                    <span>Specialty Stores</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HospitalityRetailPage;

